import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { fetchCases, fetchInsights, fetchServices } from '../api/client';
import AsyncContent from '../components/AsyncContent';
import CaseCard from '../components/CaseCard';
import CtaBand from '../components/CtaBand';
import HeroArt from '../components/HeroArt';
import InsightCard from '../components/InsightCard';
import SectionHead from '../components/SectionHead';
import ServiceCard from '../components/ServiceCard';
import { useApi } from '../hooks/useApi';
import { usePageMeta } from '../hooks/usePageMeta';
import { useLanguage } from '../i18n/LanguageProvider';

export default function HomePage() {
  const { language, t } = useLanguage();
  const copy = t.home;

  usePageMeta(copy.metaTitle, copy.metaDescription);

  const services = useApi(`services-${language}`, (signal) => fetchServices(language, signal));
  const cases = useApi(`cases-${language}`, (signal) => fetchCases(language, signal));
  const insights = useApi(`insights-${language}`, (signal) => fetchInsights(language, signal));

  return (
    <>
      <section className="hero">
        <img
          className="hero-mark"
          src="/logo-lockup.webp"
          alt=""
          aria-hidden="true"
          width={874}
          height={422}
        />
        <div className="hero-copy">
          <p className="eyebrow">{copy.eyebrow}</p>
          <h1>
            {copy.titleTop}
            <br />
            <em>{copy.titleEm}</em>
          </h1>
          <p className="lead">{copy.lead}</p>
          <div className="actions">
            <Link className="button primary" to="/kontakt">
              {copy.primaryCta}
            </Link>
            <Link className="button secondary" to="/tjanster">
              {copy.secondaryCta}
            </Link>
          </div>
          <div className="hero-meta">
            {copy.heroMeta.map((meta) => (
              <div key={meta.label}>
                <strong>{meta.value}</strong>
                {meta.label}
              </div>
            ))}
          </div>
        </div>
        <HeroArt />
      </section>

      <section className="statement">
        <p>
          {copy.statement.before}
          <strong>{copy.statement.strong}</strong>
          {copy.statement.after}
        </p>
        <h2>{copy.statement.title}</h2>
        <p className="muted">{copy.statement.muted}</p>
        <div className="chain">
          {copy.chain.map((step, index) => (
            <Fragment key={step}>
              <span>{step}</span>
              {index < copy.chain.length - 1 && <i>→</i>}
            </Fragment>
          ))}
        </div>
      </section>

      <section className="section paper">
        <SectionHead
          eyebrow={copy.problemsHead.eyebrow}
          title={copy.problemsHead.title}
          text={copy.problemsHead.text}
        />
        <div className="problems">
          {copy.problems.map((problem) => (
            <div className="problem" key={problem.quote}>
              <div>
                <q>{problem.quote}</q>
              </div>
              <div>
                <p>{problem.answer}</p>
                <span className="tag">{problem.service}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <SectionHead
          eyebrow={copy.servicesHead.eyebrow}
          title={copy.servicesHead.title}
          text={copy.servicesHead.text}
        />
        <AsyncContent state={services}>
          {(items) => (
            <div className="cards">
              {items.slice(0, 3).map((service, index) => (
                <ServiceCard key={service.id} service={service} featured={index === 1} />
              ))}
            </div>
          )}
        </AsyncContent>
        <div className="section-foot">
          <Link className="button secondary" to="/tjanster">
            {copy.allServices}
          </Link>
        </div>
      </section>

      <section className="section paper">
        <SectionHead
          eyebrow={copy.casesHead.eyebrow}
          title={copy.casesHead.title}
          text={copy.casesHead.text}
        />
        <AsyncContent state={cases}>
          {(items) => (
            <div className="case-grid">
              {items.slice(0, 2).map((item) => (
                <CaseCard key={item.id} item={item} compact />
              ))}
            </div>
          )}
        </AsyncContent>
        <div className="section-foot">
          <Link className="button secondary" to="/case">
            {copy.allCases}
          </Link>
        </div>
      </section>

      <section className="section dark">
        <div className="split">
          <div>
            <p className="eyebrow light">{copy.why.eyebrow}</p>
            <h2>
              {copy.why.titleTop}
              <br />
              {copy.why.titleBottom}
            </h2>
          </div>
          <div>
            <p className="large">{copy.why.large}</p>
            <p>{copy.why.body}</p>
            <p>
              <Link className="button ghost" to="/om-oss">
                {copy.why.link}
              </Link>
            </p>
          </div>
        </div>
        <div className="stat-grid">
          {copy.why.stats.map((stat) => (
            <div key={stat.title}>
              <strong>{stat.title}</strong>
              <span>{stat.text}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section process paper">
        <SectionHead eyebrow={copy.processHead.eyebrow} title={copy.processHead.title} />
        <div className="steps">
          {copy.workSteps.map((step) => (
            <div key={step.num}>
              <span>{step.num}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <SectionHead eyebrow={copy.insightsHead.eyebrow} title={copy.insightsHead.title} />
        <AsyncContent state={insights}>
          {(items) => (
            <div className="insight-grid">
              {items.slice(0, 3).map((insight) => (
                <InsightCard key={insight.slug} insight={insight} />
              ))}
            </div>
          )}
        </AsyncContent>
        <div className="section-foot">
          <Link className="button secondary" to="/insights">
            {copy.allInsights}
          </Link>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
